import { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { api, brl } from "../lib/api";
import { Card } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Textarea } from "../components/ui/textarea";
import { Label } from "../components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Checkbox } from "../components/ui/checkbox";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "../components/ui/dialog";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger } from "../components/ui/alert-dialog";
import { Plus, Trash2, ExternalLink, Send, Copy, FileDown, MessageCircle } from "lucide-react";
import { toast } from "sonner";

export default function ProposalEditor() {
  const { id } = useParams();
  const navigate = useNavigate();
  const isNew = !id || id === "new";
  const [library, setLibrary] = useState([]);
  const [form, setForm] = useState({
    title: "",
    client_name: "",
    client_email: "",
    client_document: "",
    client_phone: "",
    client_address: "",
    items: [],
    discount_type: "percent",
    discount_value: 0,
    tax_percent: 0,
    shipping: 0,
    validity_days: 15,
    notes: "",
    terms: "",
    execution_deadline: "",
  });
  const [data, setData] = useState(null);

  useEffect(() => {
    api.get("/items").then((r) => setLibrary(r.data));
    if (!isNew && id) api.get(`/proposals/${id}`).then((r) => { setForm(r.data); setData(r.data); });
    else api.get("/settings").then((r) => setForm((f) => ({ ...f, terms: r.data.default_terms || "", validity_days: r.data.default_validity_days || 15 })));
  }, [id, isNew]);

  const update = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const updateItem = (idx, k, v) => setForm((f) => ({ ...f, items: f.items.map((it, i) => (i === idx ? { ...it, [k]: v } : it)) }));
  const newRowId = () => `row_${Date.now()}_${Math.random().toString(36).slice(2, 8)}`;
  const addItem = (base) => setForm((f) => ({ ...f, items: [...f.items, { _rid: newRowId(), ...(base || { name: "", unit: "un", quantity: 1, price: 0, description: "", optional: false, selected: true }) }] }));
  const addFromLibrary = (item) => addItem({ name: item.name, unit: item.unit, quantity: 1, price: item.price, description: item.description, optional: false, selected: true });
  const removeItem = (idx) => setForm((f) => ({ ...f, items: f.items.filter((_, i) => i !== idx) }));

  const subtotal = form.items.filter((i) => i.selected).reduce((s, i) => s + i.quantity * i.price, 0);
  const discount = form.discount_type === "percent" ? subtotal * (form.discount_value / 100) : form.discount_value;
  const tax = Math.max(subtotal - discount, 0) * (form.tax_percent / 100);
  const total = Math.max(subtotal - discount, 0) + tax + Number(form.shipping || 0);

  const save = async () => {
    const payload = { ...form, discount_value: +form.discount_value, tax_percent: +form.tax_percent, shipping: +form.shipping, validity_days: +form.validity_days };
    if (isNew) {
      const r = await api.post("/proposals", payload);
      toast.success("Proposta criada");
      navigate(`/proposals/${r.data.proposal_id}`);
    } else {
      const r = await api.put(`/proposals/${id}`, payload);
      setData(r.data);
      toast.success("Proposta atualizada");
    }
  };

  const send = async () => {
    await save();
    await api.post(`/proposals/${data?.proposal_id || id}/send`);
    toast.success("Proposta marcada como enviada");
    const r = await api.get(`/proposals/${data?.proposal_id || id}`);
    setData(r.data);
    setForm(r.data);
  };

  const remove = async () => {
    await api.delete(`/proposals/${data?.proposal_id || id}`);
    toast.success("Proposta excluída");
    navigate("/proposals");
  };

  const publicUrl = data ? `${window.location.origin}/p/${data.public_token}` : "";
  const wappUrl = data && form.client_phone ? `https://wa.me/${form.client_phone.replace(/\D/g, "")}?text=${encodeURIComponent(`Olá ${form.client_name}, segue proposta: ${publicUrl}`)}` : "";

  return (
    <div className="p-10 max-w-[1400px]">
      <div className="flex items-end justify-between mb-8">
        <div>
          <p className="overline">{data?.number || "Nova Proposta"}</p>
          <h1 className="font-heading text-4xl mt-2">{isNew ? "Criar Orçamento" : "Editar Proposta"}</h1>
        </div>
        <div className="flex gap-2">
          {data && (
            <>
              <Button variant="outline" asChild data-testid="view-public-btn">
                <a href={publicUrl} target="_blank" rel="noreferrer"><ExternalLink size={14} className="mr-1" /> Visualizar como cliente</a>
              </Button>
              <Button variant="outline" onClick={() => { navigator.clipboard.writeText(publicUrl); toast.success("Link copiado"); }} data-testid="copy-link-btn">
                <Copy size={14} className="mr-1" /> Copiar link
              </Button>
              {wappUrl && (
                <Button variant="outline" asChild data-testid="whatsapp-btn">
                  <a href={wappUrl} target="_blank" rel="noreferrer"><MessageCircle size={14} className="mr-1" /> WhatsApp</a>
                </Button>
              )}
              <Button variant="outline" asChild data-testid="pdf-btn">
                <a href={`${process.env.REACT_APP_BACKEND_URL}/api/proposals/${data.proposal_id}/pdf`} target="_blank" rel="noreferrer"><FileDown size={14} className="mr-1" /> PDF</a>
              </Button>
              <Button className="bg-[#D4AF37] hover:bg-[#B8961F] text-white" onClick={send} data-testid="send-btn"><Send size={14} className="mr-1" /> Enviar</Button>
              <AlertDialog>
                <AlertDialogTrigger asChild>
                  <Button variant="outline" className="border-red-200 text-red-600 hover:bg-red-50 hover:text-red-700" data-testid="delete-proposal-btn">
                    <Trash2 size={14} className="mr-1" /> Excluir
                  </Button>
                </AlertDialogTrigger>
                <AlertDialogContent>
                  <AlertDialogHeader>
                    <AlertDialogTitle>Excluir proposta {data.number}?</AlertDialogTitle>
                    <AlertDialogDescription>
                      Esta ação é permanente. A proposta "{data.title}" e todo o seu histórico (visualizações, assinaturas, itens) serão removidos. Contratos vinculados permanecem no sistema.
                    </AlertDialogDescription>
                  </AlertDialogHeader>
                  <AlertDialogFooter>
                    <AlertDialogCancel data-testid="cancel-delete-proposal">Cancelar</AlertDialogCancel>
                    <AlertDialogAction className="bg-red-600 hover:bg-red-700 text-white" onClick={remove} data-testid="confirm-delete-proposal">
                      Excluir permanentemente
                    </AlertDialogAction>
                  </AlertDialogFooter>
                </AlertDialogContent>
              </AlertDialog>
            </>
          )}
          <Button className="bg-[#C07C88] hover:bg-[#A86571]" onClick={save} data-testid="save-btn">Salvar</Button>
        </div>
      </div>

      <div className="grid lg:grid-cols-3 gap-6">
        <div className="lg:col-span-2 space-y-6">
          <Card className="p-6 shadow-none border-border">
            <p className="overline mb-4">Dados da proposta</p>
            <div className="grid grid-cols-2 gap-4">
              <div className="col-span-2">
                <Label>Título da proposta</Label>
                <Input value={form.title} onChange={(e) => update("title", e.target.value)} placeholder="Ex: Redesenho do site institucional" data-testid="input-title" />
              </div>
              <div><Label>Cliente</Label><Input value={form.client_name} onChange={(e) => update("client_name", e.target.value)} data-testid="input-client-name" /></div>
              <div><Label>E-mail</Label><Input type="email" value={form.client_email} onChange={(e) => update("client_email", e.target.value)} data-testid="input-client-email" /></div>
              <div><Label>CPF/CNPJ</Label><Input value={form.client_document} onChange={(e) => update("client_document", e.target.value)} data-testid="input-client-doc" /></div>
              <div><Label>Telefone/WhatsApp</Label><Input value={form.client_phone} onChange={(e) => update("client_phone", e.target.value)} placeholder="5511999999999" data-testid="input-client-phone" /></div>
              <div className="col-span-2"><Label>Endereço</Label><Input value={form.client_address} onChange={(e) => update("client_address", e.target.value)} data-testid="input-client-address" /></div>
              <div><Label>Prazo de execução</Label><Input value={form.execution_deadline} onChange={(e) => update("execution_deadline", e.target.value)} placeholder="Ex: 30 dias" data-testid="input-deadline" /></div>
              <div><Label>Validade (dias)</Label><Input type="number" value={form.validity_days} onChange={(e) => update("validity_days", e.target.value)} data-testid="input-validity" /></div>
            </div>
          </Card>

          <Card className="p-6 shadow-none border-border">
            <div className="flex items-center justify-between mb-4">
              <p className="overline">Itens do orçamento</p>
              <div className="flex gap-2">
                <Dialog>
                  <DialogTrigger asChild>
                    <Button variant="outline" size="sm" data-testid="lib-btn"><Plus size={14} className="mr-1" /> Da biblioteca</Button>
                  </DialogTrigger>
                  <DialogContent className="max-w-2xl">
                    <DialogHeader><DialogTitle>Biblioteca de serviços</DialogTitle></DialogHeader>
                    <div className="max-h-96 overflow-auto divide-y">
                      {library.map((it) => (
                        <div key={it.item_id} className="py-3 flex items-center justify-between">
                          <div>
                            <p className="font-medium text-sm">{it.name}</p>
                            <p className="text-xs text-muted-foreground">{it.description}</p>
                          </div>
                          <div className="flex items-center gap-3">
                            <span className="text-sm font-medium">{brl(it.price)}<span className="text-xs text-muted-foreground">/{it.unit}</span></span>
                            <Button size="sm" onClick={() => addFromLibrary(it)} data-testid={`add-lib-${it.item_id}`}>Adicionar</Button>
                          </div>
                        </div>
                      ))}
                    </div>
                  </DialogContent>
                </Dialog>
                <Button variant="outline" size="sm" onClick={() => addItem()} data-testid="add-item-btn"><Plus size={14} className="mr-1" /> Item vazio</Button>
              </div>
            </div>
            <div className="space-y-3">
              {form.items.map((it, idx) => (
                <div key={it._rid || `item-${idx}`} className="grid grid-cols-12 gap-2 items-center pb-3 border-b border-border last:border-0" data-testid={`item-row-${idx}`}>
                  <Input className="col-span-4" placeholder="Item" value={it.name} onChange={(e) => updateItem(idx, "name", e.target.value)} />
                  <Input className="col-span-2" placeholder="Unidade" value={it.unit} onChange={(e) => updateItem(idx, "unit", e.target.value)} />
                  <Input className="col-span-1" type="number" placeholder="Qtd" value={it.quantity} onChange={(e) => updateItem(idx, "quantity", +e.target.value)} />
                  <Input className="col-span-2" type="number" step="0.01" placeholder="Valor" value={it.price} onChange={(e) => updateItem(idx, "price", +e.target.value)} />
                  <div className="col-span-2 flex items-center gap-2 text-xs">
                    <Checkbox checked={it.optional} onCheckedChange={(v) => updateItem(idx, "optional", !!v)} id={`opt-${idx}`} />
                    <Label htmlFor={`opt-${idx}`} className="cursor-pointer">Opcional</Label>
                  </div>
                  <button className="col-span-1 p-2 text-muted-foreground hover:text-red-500" onClick={() => removeItem(idx)} data-testid={`remove-item-${idx}`}>
                    <Trash2 size={14} />
                  </button>
                </div>
              ))}
              {form.items.length === 0 && <p className="text-sm text-muted-foreground py-6 text-center">Nenhum item. Use a biblioteca ou adicione manualmente.</p>}
            </div>
          </Card>

          <Card className="p-6 shadow-none border-border">
            <p className="overline mb-4">Observações e termos</p>
            <div className="space-y-4">
              <div><Label>Observações internas</Label><Textarea value={form.notes} onChange={(e) => update("notes", e.target.value)} rows={3} data-testid="input-notes" /></div>
              <div><Label>Termos e condições</Label><Textarea value={form.terms} onChange={(e) => update("terms", e.target.value)} rows={4} data-testid="input-terms" /></div>
            </div>
          </Card>
        </div>

        <div>
          <Card className="p-6 shadow-none border-border sticky top-6">
            <p className="overline mb-4">Resumo financeiro</p>
            <div className="grid grid-cols-2 gap-3 mb-4">
              <div>
                <Label className="text-xs">Desconto</Label>
                <Select value={form.discount_type} onValueChange={(v) => update("discount_type", v)}>
                  <SelectTrigger data-testid="discount-type"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="percent">%</SelectItem>
                    <SelectItem value="fixed">R$</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div><Label className="text-xs">Valor</Label><Input type="number" value={form.discount_value} onChange={(e) => update("discount_value", e.target.value)} data-testid="discount-value" /></div>
              <div><Label className="text-xs">Imposto (%)</Label><Input type="number" value={form.tax_percent} onChange={(e) => update("tax_percent", e.target.value)} data-testid="tax-percent" /></div>
              <div><Label className="text-xs">Envio (R$)</Label><Input type="number" value={form.shipping} onChange={(e) => update("shipping", e.target.value)} data-testid="shipping" /></div>
            </div>
            <div className="text-sm space-y-2 border-t border-border pt-4">
              <div className="flex justify-between"><span className="text-muted-foreground">Subtotal</span><span>{brl(subtotal)}</span></div>
              <div className="flex justify-between"><span className="text-muted-foreground">Desconto</span><span>- {brl(discount)}</span></div>
              <div className="flex justify-between"><span className="text-muted-foreground">Imposto</span><span>{brl(tax)}</span></div>
              <div className="flex justify-between"><span className="text-muted-foreground">Envio</span><span>{brl(+form.shipping || 0)}</span></div>
              <div className="flex justify-between pt-3 border-t border-border mt-3">
                <span className="font-heading text-base">TOTAL</span>
                <span className="font-heading text-2xl text-[#D4AF37]" data-testid="total-amount">{brl(total)}</span>
              </div>
            </div>
          </Card>
        </div>
      </div>
    </div>
  );
}
